import React from 'react';
import styled, { keyframes } from 'styled-components';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Base skeleton element
const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
  
  /* Dark theme for mobile */
  @media (max-width: 768px) {
    background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
    background-size: 200px 100%;
  }
`;

// Mobile grid skeleton
const MobileSkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  padding: 0;
  margin: 0;
  width: 100%;
`;

const MobileSkeletonItem = styled(SkeletonBase)`
  aspect-ratio: 9/16;
  position: relative;
  background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
  background-size: 200px 100%;
`;

const MobileSkeletonHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SkeletonButton = styled(SkeletonBase)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
  background-size: 200px 100%;
`;

const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '16px'};
  background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
  background-size: 200px 100%;
`;

// Desktop grid skeleton
const DesktopSkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  padding-bottom: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DesktopSkeletonCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const DesktopSkeletonImage = styled(SkeletonBase)`
  height: 240px;
  border-radius: 0;
`;

const DesktopSkeletonContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Component exports
export interface MobileGridSkeletonProps {
  count?: number;
  showHeader?: boolean;
  headerTitle?: string;
}

export const MobileGridSkeleton: React.FC<MobileGridSkeletonProps> = ({ 
  count = 9, 
  showHeader = true,
  headerTitle = "Loading..."
}) => {
  return (
    <>
      {showHeader && (
        <MobileSkeletonHeader>
          <SkeletonButton />
          <div style={{ flex: 1 }}>
            <SkeletonText width="120px" height="20px" style={{ marginBottom: '4px' }} />
            <SkeletonText width="80px" height="14px" />
          </div>
        </MobileSkeletonHeader>
      )}
      <MobileSkeletonGrid>
        {Array.from({ length: count }, (_, index) => (
          <MobileSkeletonItem key={index} />
        ))}
      </MobileSkeletonGrid>
    </>
  );
};

export interface DesktopGridSkeletonProps {
  count?: number;
}

export const DesktopGridSkeleton: React.FC<DesktopGridSkeletonProps> = ({ count = 6 }) => {
  return (
    <DesktopSkeletonGrid>
      {Array.from({ length: count }, (_, index) => (
        <DesktopSkeletonCard key={index}>
          <DesktopSkeletonImage />
          <DesktopSkeletonContent>
            <SkeletonText height="24px" width="85%" />
            <SkeletonText height="16px" width="60%" />
            <SkeletonText height="14px" width="40%" />
          </DesktopSkeletonContent>
        </DesktopSkeletonCard>
      ))}
    </DesktopSkeletonGrid>
  );
};

// Progressive loading skeleton - hiển thị các items đã load
export interface ProgressiveSkeletonProps {
  totalCount: number;
  loadedCount: number;
  renderItem: (index: number) => React.ReactNode;
  renderSkeleton: (index: number) => React.ReactNode;
  gridClassName?: string;
}

export const ProgressiveSkeleton: React.FC<ProgressiveSkeletonProps> = ({
  totalCount,
  loadedCount,
  renderItem,
  renderSkeleton,
  gridClassName
}) => {
  const items = Array.from({ length: totalCount }, (_, index) => {
    if (index < loadedCount) {
      return renderItem(index);
    } else {
      return renderSkeleton(index);
    }
  });

  return (
    <div className={gridClassName}>
      {items}
    </div>
  );
};

// Mixed skeleton - một số item real, một số skeleton
export interface MixedLoadingGridProps {
  loadedItems: React.ReactNode[];
  skeletonCount: number;
  isMobile?: boolean;
}

export const MixedLoadingGrid: React.FC<MixedLoadingGridProps> = ({
  loadedItems,
  skeletonCount,
  isMobile = false
}) => {
  const GridContainer = isMobile ? MobileSkeletonGrid : DesktopSkeletonGrid;
  const SkeletonItem = isMobile ? MobileSkeletonItem : DesktopSkeletonCard;
  
  return (
    <GridContainer>
      {loadedItems}
      {Array.from({ length: skeletonCount }, (_, index) => (
        <SkeletonItem key={`skeleton-${index}`}>
          {!isMobile && (
            <>
              <DesktopSkeletonImage />
              <DesktopSkeletonContent>
                <SkeletonText height="24px" width="85%" />
                <SkeletonText height="16px" width="60%" />
                <SkeletonText height="14px" width="40%" />
              </DesktopSkeletonContent>
            </>
          )}
        </SkeletonItem>
      ))}
    </GridContainer>
  );
};

const SkeletonLoader = {
  MobileGridSkeleton,
  DesktopGridSkeleton,
  ProgressiveSkeleton,
  MixedLoadingGrid
};

export default SkeletonLoader; 